import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ManualOrderRequest, Order } from '../../shared/models/Order';
import { SpringPage } from '../../shared/models/page';
import { Auth } from './auth.service';
import { environment } from '../../../environments/environment.dev';

export interface MercadoPagoCheckoutResponse {
  orderId: number;
  preferenceId: string;
  initPoint: string;
  sandboxInitPoint?: string;
  totalAmount: number;
  paymentStatus?: string;
  orderStatus?: string;
}

export type MercadoPagoPaymentState = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

export interface OrderAdminQuery {
  page?: number;
  size?: number;
  canal?: string;
  isManualSale?: boolean;
  vista?: string;
  status?: string | string[];
  from?: string;
  to?: string;
  q?: string;
  orderId?: number;
  tipoEnvioId?: number;
  origenVentaId?: number;
  estadoIds?: number[];
  scheduledDate?: string;
  guiaImpresa?: boolean;
}

export interface OrderAdminKpisDto {
  pedidos: number;
  totalAdelantado: number;
  totalEnvioAdelantado: number;
  totalVentas: number;
  totalPagoPendiente: number;
  netoCobrado: number;
  comisionesMp: number;
  cobradoPeriodo: number;
  totalVentasEntregadas: number;
  totalEnvioEntregadas: number;
  netoCobradoEntregadas: number;
  comisionesMpEntregadas: number;
}

export function emptyOrderAdminKpis(): OrderAdminKpisDto {
  return {
    pedidos: 0,
    totalAdelantado: 0,
    totalEnvioAdelantado: 0,
    totalVentas: 0,
    totalPagoPendiente: 0,
    netoCobrado: 0,
    comisionesMp: 0,
    cobradoPeriodo: 0,
    totalVentasEntregadas: 0,
    totalEnvioEntregadas: 0,
    netoCobradoEntregadas: 0,
    comisionesMpEntregadas: 0
  };
}

export interface MercadoPagoOrderStatus {
  orderId: number;
  status: MercadoPagoPaymentState;
  mpStatus?: string | null;
  paymentStatus?: string | null;
  orderStatus?: string | null;
  retryable: boolean;
  initPoint?: string | null;
  expiresAt?: string | null;
  paidAt?: string | null;
  totalAmount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  private apiUrl = `${environment.apiUrl}/orders`;

  constructor(private http: HttpClient, private auth: Auth) { }

  private getHeaders(): HttpHeaders {
    const token = this.auth.getToken();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  createManualOrder(order: ManualOrderRequest): Observable<Order> {
    return this.http.post<Order>(`${this.apiUrl}/manual`, order, { headers: this.getHeaders() });
  }

  updateOrder(id: number, order: ManualOrderRequest): Observable<Order> {
    return this.http.put<Order>(`${this.apiUrl}/${id}`, order, { headers: this.getHeaders() });
  }

  getUpcomingOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(`${this.apiUrl}/upcoming`, { headers: this.getHeaders() });
  }

  getAllOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(this.apiUrl, { headers: this.getHeaders() });
  }

  getAdminOrders(query: OrderAdminQuery): Observable<SpringPage<Order>> {
    return this.http.get<SpringPage<Order>>(`${this.apiUrl}/admin`, {
      params: this.toAdminParams(query, true),
      headers: this.getHeaders()
    }).pipe(map(page => this.normalizePage(page)));
  }

  getAdminKpis(query: OrderAdminQuery): Observable<OrderAdminKpisDto> {
    return this.http.get<Partial<OrderAdminKpisDto>>(`${this.apiUrl}/admin/kpis`, {
      params: this.toAdminParams(query, false),
      headers: this.getHeaders()
    }).pipe(map(body => this.normalizeKpis(body)));
  }

  exportAdminOrders(query: OrderAdminQuery): Observable<Order[]> {
    return this.http.get<Order[]>(`${this.apiUrl}/admin/export`, {
      params: this.toAdminParams(query, false),
      headers: this.getHeaders()
    }).pipe(map(rows => (rows || []).map(row => this.normalizeListRow(row))));
  }

  private toAdminParams(query: OrderAdminQuery, paging: boolean): HttpParams {
    let params = new HttpParams();
    const entries: [string, string | number | boolean | undefined | null][] = [
      ['canal', query.canal],
      ['isManualSale', query.isManualSale],
      ['vista', query.vista],
      ['from', query.from],
      ['to', query.to],
      ['q', query.q],
      ['orderId', query.orderId],
      ['tipoEnvioId', query.tipoEnvioId],
      ['origenVentaId', query.origenVentaId],
      ['scheduledDate', query.scheduledDate],
      ['guiaImpresa', query.guiaImpresa]
    ];
    if (paging) {
      entries.unshift(['page', query.page ?? 0], ['size', query.size ?? 10]);
    }
    for (const [key, value] of entries) {
      if (value === undefined || value === null || value === '') continue;
      params = params.set(key, String(value));
    }
    params = this.appendList(params, 'status', query.status);
    params = this.appendList(params, 'estadoIds', query.estadoIds);
    return params;
  }

  private appendList(
    params: HttpParams,
    key: string,
    value?: string | number | Array<string | number> | null
  ): HttpParams {
    if (value === undefined || value === null || value === '') return params;
    const list = Array.isArray(value) ? value : [value];
    for (const item of list) {
      if (item === undefined || item === null || item === '') continue;
      params = params.append(key, String(item));
    }
    return params;
  }

  private normalizeKpis(raw: Partial<OrderAdminKpisDto> | null | undefined): OrderAdminKpisDto {
    const source = raw || {};
    const n = (value: unknown): number => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };
    return {
      pedidos: n(source.pedidos),
      totalAdelantado: n(source.totalAdelantado),
      totalEnvioAdelantado: n(source.totalEnvioAdelantado),
      totalVentas: n(source.totalVentas),
      totalPagoPendiente: n(source.totalPagoPendiente),
      netoCobrado: n(source.netoCobrado),
      comisionesMp: n(source.comisionesMp),
      cobradoPeriodo: n(source.cobradoPeriodo),
      totalVentasEntregadas: n(source.totalVentasEntregadas),
      totalEnvioEntregadas: n(source.totalEnvioEntregadas),
      netoCobradoEntregadas: n(source.netoCobradoEntregadas),
      comisionesMpEntregadas: n(source.comisionesMpEntregadas)
    };
  }

  getOrderById(id: number): Observable<Order> {
    return this.http.get<Order>(`${this.apiUrl}/${id}`, { headers: this.getHeaders() });
  }

  updateStatus(id: number, status: string): Observable<Order> {
    return this.http.patch<Order>(`${this.apiUrl}/${id}/status?status=${status}`, {}, { headers: this.getHeaders() })
      .pipe(map(row => this.normalizeListRow(row)));
  }

  updateEstados(id: number, estadoIds: number[]): Observable<Order> {
    return this.http.patch<Order>(`${this.apiUrl}/${id}/estados`, estadoIds, { headers: this.getHeaders() })
      .pipe(map(row => this.normalizeListRow(row)));
  }

  updateDocumento(id: number, dni: string): Observable<Order> {
    return this.http.patch<Order>(`${this.apiUrl}/${id}/documento`, { dni }, { headers: this.getHeaders() });
  }

  marcarGuiaImpresa(id: number, impresa = true): Observable<Order> {
    return this.http.patch<Order>(`${this.apiUrl}/${id}/guia-impresa`, { impresa }, { headers: this.getHeaders() });
  }

  verifyWebPayment(id: number, approved: boolean): Observable<Order> {
    return this.http.patch<Order>(`${this.apiUrl}/${id}/verify?approved=${approved}`, {}, { headers: this.getHeaders() });
  }

  createWebOrder(orderData: any, file?: File | null): Observable<any> {
    const formData = new FormData();
    formData.append('order', new Blob([JSON.stringify(orderData)], { type: 'application/json' }));
    if (file) {
      formData.append('file', file);
    }
    return this.http.post(`${this.apiUrl}/web`, formData, { headers: this.getHeaders() });
  }

  createMercadoPagoCheckout(orderData: any): Observable<MercadoPagoCheckoutResponse> {
    const idempotencyKey = this.buildCheckoutIdempotencyKey(orderData);
    const headers = this.getHeaders().set('Idempotency-Key', idempotencyKey);
    return this.http.post<MercadoPagoCheckoutResponse>(
      `${environment.apiUrl}/payments/mercadopago/checkout`,
      orderData,
      { headers }
    );
  }

  private buildCheckoutIdempotencyKey(orderData: any): string {
    const items = Array.isArray(orderData?.items) ? orderData.items : [];
    const fingerprint = items
      .map((i: any) => `${i.productId || 0}:${i.size || ''}:${i.quantity || 0}`)
      .sort()
      .join('|');
    const tipo = orderData?.tipoEnvioId ?? 0;
    const stable = `mp:${tipo}:${fingerprint}`;
    try {
      const existing = sessionStorage.getItem('mp-idempotency');
      const parsed = existing ? JSON.parse(existing) : null;
      if (parsed?.stable === stable && parsed?.key && (Date.now() - (parsed.ts || 0)) < 5 * 60 * 1000) {
        return parsed.key;
      }
      const key = `mp-${Math.abs(this.hashString(stable)).toString(16)}-${Date.now().toString(36)}`;
      sessionStorage.setItem('mp-idempotency', JSON.stringify({ stable, key, ts: Date.now() }));
      return key;
    } catch {
      return `mp-${Math.abs(this.hashString(stable)).toString(16)}`;
    }
  }

  private hashString(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = ((hash << 5) - hash) + value.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  }

  syncMercadoPagoPayment(paymentId: string): Observable<Order> {
    return this.http.post<Order>(
      `${environment.apiUrl}/payments/mercadopago/sync/${encodeURIComponent(paymentId)}`,
      {},
      { headers: this.getHeaders() }
    );
  }

  syncMercadoPagoOrder(orderId: number): Observable<Order> {
    return this.http.post<Order>(
      `${environment.apiUrl}/payments/mercadopago/sync/order/${orderId}`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getMercadoPagoOrderStatus(orderId: number | string): Observable<MercadoPagoOrderStatus> {
    return this.http.get<MercadoPagoOrderStatus>(
      `${environment.apiUrl}/payments/mercadopago/orders/${orderId}/status`,
      { headers: this.getHeaders() }
    );
  }

  retryMercadoPagoPayment(orderId: number | string): Observable<MercadoPagoOrderStatus> {
    return this.http.post<MercadoPagoOrderStatus>(
      `${environment.apiUrl}/payments/mercadopago/orders/${orderId}/retry`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getNewPaidWebOrdersCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${environment.apiUrl}/payments/mercadopago/admin/new-paid-count`,
      { headers: this.getHeaders() }
    );
  }

  getMercadoPagoPanelConfig(): Observable<{ paymentUrlBase: string }> {
    return this.http.get<{ paymentUrlBase: string }>(
      `${environment.apiUrl}/payments/mercadopago/admin/panel-config`,
      { headers: this.getHeaders() }
    );
  }

  markNewPaidWebOrdersSeen(): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>(
      `${environment.apiUrl}/payments/mercadopago/admin/new-paid/seen`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getMyOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(`${this.apiUrl}/my-orders`, { headers: this.getHeaders() });
  }

  private normalizePage(page: SpringPage<Order> | null | undefined): SpringPage<Order> {
    const content = (page?.content || []).map(row => this.normalizeListRow(row));
    return {
      content,
      totalElements: page?.totalElements ?? content.length,
      totalPages: page?.totalPages ?? 0,
      number: page?.number ?? 0,
      size: page?.size ?? content.length,
      first: page?.first,
      last: page?.last,
      empty: page?.empty
    };
  }

  private normalizeListRow(raw: Order | null | undefined): Order {
    const source = (raw || {}) as Order & {
      boleta?: { id?: number; boletaId?: number; tieneBoleta?: boolean; numeroCompleto?: string; envioCliente?: string };
      boletaStatus?: { id?: number; boletaId?: number; tieneBoleta?: boolean; numeroCompleto?: string; envioCliente?: string };
      numeroBoleta?: string;
      itemSummaries?: any[];
    };
    const boleta = source.boleta || source.boletaStatus;
    const rawItems = Array.isArray(source.items)
      ? source.items
      : Array.isArray(source.itemSummaries)
        ? source.itemSummaries
        : [];
    const items = rawItems.map((item: any) => {
      const productName = item?.productName || item?.nombre || item?.name || item?.product?.productName || item?.product?.name;
      return {
        ...item,
        productName,
        product: item?.product || (productName ? { productName, name: productName } : undefined)
      };
    });
    return {
      ...source,
      items,
      tieneBoleta: source.tieneBoleta ?? boleta?.tieneBoleta,
      boletaId: source.boletaId ?? boleta?.boletaId ?? boleta?.id,
      numeroCompleto: source.numeroCompleto ?? source.numeroBoleta ?? boleta?.numeroCompleto,
      envioCliente: source.envioCliente ?? boleta?.envioCliente
    };
  }

  updateSchedule(orderId: number, date: string, time: string): Observable<any> {
    const body = {
      date: date,
      time: time
    };
    return this.http.put(`${this.apiUrl}/${orderId}/schedule`, body, { headers: this.getHeaders() });
  }
}